import { useState, useRef, useEffect } from 'react';
import mapboxgl, { GeoJSONSource } from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { observer } from 'mobx-react-lite';
import { LocateFixed } from 'lucide-react';
import giftIcon from '../../assets/images/pngs/gift.png';
import { toastify } from '@/utils/toastify';
import logoIcon from '@/assets/images/pngs/OMIKUJI-Main-Logo.png';
import markerIcon from '@/assets/images/pngs/Marker.png';
import stackIcon from '@/assets/images/pngs/Stack.png';
import dropIcon from '@/assets/images/pngs/Drop.png';
import { t } from 'i18next';
import menuIconSvg from '@/assets/images/svgs/Menu.svg';
import { ROUTES } from '@/configs/constants';
import { useLocation, useNavigate } from 'react-router-dom';
import { useStore } from '@/hooks/useStore';
import { Button } from '@/components/common/Button';
import mapPinnedImg from '@/assets/images/pngs/map-pinned.png';
import { ItemOnMap, Location, OwnerItem, ParamsGetItemNear } from '@/types/item';
import { LocationService } from '@/services/location-service';
import mapPinIcon from '@/assets/images/pngs/MapPinLine.png';
import CheckboxComponent from '@/components/common/CheckBox';
import { flowResult } from 'mobx';
import { cn, handleLongText, haversineDistance } from '@/utils/utils';
import popupImg from '@/assets/images/pngs/popup-item.png';
import imgArrowRight from '@/assets/images/pngs/arrow-right.png';
import itemImg from '@/assets/images/pngs/item1.png';
import UseShakeDetection from '@/utils/shake';
import moveToFindGif from '@/assets/images/gifs/move-to-find.gif';
import shakeToGetGif from '@/assets/images/gifs/shake-to-get.gif';
import loadingImg from '@/assets/images/pngs/loading.png';
import { getStorage, saveLocalStorage } from '@/utils/browsers';

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_PUBKEY;
interface DeviceMotionEvent {
    requestPermission?: () => Promise<'granted' | 'denied'>
}
const sourceId = 'markers-layer';
const sourceIdItemOnMap = 'markers-layer-item-on-map';

export default observer(function HomePage() {
    const {
        authStore: { login, getMyInfo },
        modalStore: { showModal, hideModal },
        apiStore: { setLoading },
        itemsStore: { getListNearBy, listNearBy, getDetailItemCollected, idDetail, setIdDetail,
            itemLocations, setItemLocations, itemToSetLocation, listItemToSetLocation }
    } = useStore();

    let timeoutNoItem: ReturnType<typeof setTimeout> | null = null;

    const mapContainer = useRef<HTMLDivElement>(null);
    const map = useRef<mapboxgl.Map | null>(null);
    const navigate = useNavigate();
    const location = useLocation();
    const [userLocation, setUserLocation] = useState<[number, number] | null>(null);
    const marker = useRef<mapboxgl.Marker | null>(null);
    const circleSource = useRef<GeoJSONSource | null>(null);

    const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
    const [onAddGift, setOnAddGift] = useState(false);
    const [markers, setMarkers] = useState<any[]>([]);
    // const { item } = location.state || {};
    // const { listItems } = location.state || [];
    const [isShowingModalMove, setIsShowingModalMove] = useState<boolean>(false);
    const [showCountItem, setShowCountItem] = useState<boolean>(false);
    const [isShakeEnabled, setIsShakeEnabled] = useState<boolean>(false);

    const [isIOSAdd, setIsIOSAdd] = useState(false);
    const [isStandalone, setIsStandalone] = useState(false);
    useEffect(() => {
        // Kiểm tra nếu là iOS
        const userAgent = window.navigator.userAgent.toLowerCase();
        setIsIOSAdd(/iphone|ipad|ipod/.test(userAgent));
        setIsStandalone(window.matchMedia('(display-mode: standalone)').matches);
    }, []);

    useEffect(() => {
        getMyInfo();
        if (map.current && map.current.getLayer(sourceIdItemOnMap) && !idDetail && !itemLocations) {
            map.current.removeLayer(sourceIdItemOnMap);
            map.current.removeSource(sourceIdItemOnMap);
        }
    }, []);

    useEffect(() => {
        const enableClick = () => {
            if (map.current) {
                handleAddPoint();
            } else {
                setTimeout(() => {
                    enableClick();
                }, 240);
            }
        };
        if (itemToSetLocation) {
            enableClick();
        }
    }, [itemToSetLocation]);

    useEffect(() => {
        if (idDetail) {
            setShowCountItem(false);
            addItemOnMap(idDetail);
            setIdDetail(null);
        }
        if (itemLocations) {
            setLoading(true);
            setShowCountItem(false);
            handleShowItemAssign(itemLocations);
            setItemLocations(null);
        }
    }, [idDetail, itemLocations]);
    const addItemOnMap = async (id) => {
        if (!map.current) {
            setTimeout(() => {
                addItemOnMap(id);
                return;
            }, 1000);
        }
        const res = await flowResult(getDetailItemCollected(id));
        addMarkerItem([res]);
    };

    const handleShowItemAssign = (data) => {
        if (!map.current) {
            setTimeout(() => {
                handleShowItemAssign(data);
                return;
            }, 1000);
        } else {
            setLoading(false);
            addMarkerItem(data);
        }
    };

    const getCircleRadius = (zoom: number): number => {
        const baseRadius = 30;
        const metersPerPixel = userLocation ?
            156543.03392 * Math.cos(userLocation[1] * Math.PI / 180) / Math.pow(2, zoom) :
            0;
        return metersPerPixel ? baseRadius / metersPerPixel : 0;
    };

    const handleMotionEvent = () => {
        setIsShakeEnabled(true);
    };

    const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

    const handleWhenShake = async () => {
        setIsShakeEnabled(false);
        hideModal('shake-to-get');
        // Đợi 240ms trước khi hiển thị modal
        await delay(240);
        showModal({
            id: 'geting-near-items',
            showCloseIcon: false,
            saveButton: false,
            cancelButton: false,
            content: (
                <div className='w-full flex flex-col justify-center items-center'>
                    <img src={loadingImg} alt={loadingImg} className='w-[90px] h-[90px] animate-spin-reverse' />
                    <p className='text-[15px] font-semibold text-gray mt-6'>{t('text.getingNear')}</p>
                </div>
            )
        });

        // Đợi cho đến khi âm thanh kết thúc rồi mới chuyển trang
        await handleVibrate();

        navigate(ROUTES.listNearBy.href);
    };

    UseShakeDetection({ onShake: handleWhenShake, enabled: isShakeEnabled, threshold: 10, timeout: 1000 });

    const requestPermission = async () => {
        if (typeof (DeviceMotionEvent as any).requestPermission === 'function') {
            try {
                const permission = await (DeviceMotionEvent as any).requestPermission();
                if (permission === 'granted') {
                    window.addEventListener('devicemotion', handleMotionEvent);
                }
            } catch (error) {
                toastify('error', '許可リクエスト中にエラーが発生しました');
            }
        } else {
            window.addEventListener('devicemotion', handleMotionEvent);
        }
    };

    useEffect(() => {
        if (!isIOS) {
            window.addEventListener('devicemotion', handleMotionEvent);
        }
        if ('geolocation' in navigator) {
            const watchId = navigator.geolocation.watchPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    const newLocation: [number, number] = [longitude, latitude];
                    if (
                        !userLocation ||
                        Math.abs(newLocation[0] - userLocation[0]) > 0.00001 ||
                        Math.abs(newLocation[1] - userLocation[1]) > 0.00001
                    ) {
                        setUserLocation(newLocation);
                    }
                    // setUserLocation([139.692912, 35.688985]);
                },
                (error) => {
                    toastify('error', 'GPS 位置情報の取得中にエラーが発生しました');
                },
                {
                    enableHighAccuracy: true,
                    maximumAge: 0,
                    timeout: 10000
                }
            );

            return () => navigator.geolocation.clearWatch(watchId);
        } else {
            toastify('error', 'このブラウザでは地理位置情報がサポートされていません');
        }
    }, []);

    const handleVibrate = async () => {
        if ('vibrate' in navigator) {
            await new Promise<void>((resolve) => {
                navigator.vibrate([200, 100, 200, 200, 100, 200]);
                resolve();
            });
        }
        await new Promise<void>((resolve) => {
            playHapticSound();
            resolve();
        });
    };

    // Hàm phát âm thanh rung nhẹ (haptic sound)
    const playHapticSound = async () => {
        return new Promise<void>((resolve, reject) => {
            const audio = new Audio('/haptic.mp3');
            audio.play()
                .then(() => {
                    audio.onended = () => {
                        resolve(); // Khi âm thanh phát xong, resolve promise
                    };
                })
                .catch((error) => {
                    console.error('Lỗi phát âm thanh:', error);
                    reject(error); // Nếu có lỗi khi phát âm thanh, reject promise
                });
        });
    };

    useEffect(() => {
        if (!userLocation || !mapContainer.current) return;

        if (map.current) {
            // Nếu map đã tồn tại, kiểm tra xem container có đúng không
            if (map.current.getContainer() !== mapContainer.current) {
                map.current.remove(); // Cleanup nếu container bị lỗi
                map.current = null;
            } else {
                return; // Nếu map đã tồn tại và đúng container thì không cần tạo lại
            }
        }
        try {
            setLoading(true);
            map.current = new mapboxgl.Map({
                container: mapContainer.current,
                style: 'mapbox://styles/mapbox/streets-v12',
                center: userLocation,
                zoom: 16,
                minZoom: 14,
                maxZoom: 18,
                preserveDrawingBuffer: true
            });

            const markerElement = document.createElement('div');
            markerElement.style.width = '16px';
            markerElement.style.height = '16px';
            markerElement.style.borderRadius = '50%';
            markerElement.style.backgroundColor = '#007bff';
            markerElement.style.border = '2px solid #ffffff';
            markerElement.style.boxShadow = '0 0 4px rgba(0, 0, 0, 0.3)';

            marker.current = new mapboxgl.Marker({
                element: markerElement
            })
                .setLngLat(userLocation)
                .addTo(map.current);

            map.current.on('load', () => {
                map.current?.addSource('circleSource', {
                    type: 'geojson',
                    data: {
                        type: 'FeatureCollection',
                        features: [
                            {
                                type: 'Feature',
                                geometry: {
                                    type: 'Point',
                                    coordinates: userLocation
                                },
                                properties: {}
                            }
                        ]
                    }
                });

                map.current?.addLayer({
                    id: 'gpsArea',
                    type: 'circle',
                    source: 'circleSource',
                    paint: {
                        'circle-color': '#007bff',
                        'circle-opacity': 0.2,
                        'circle-radius': userLocation ? getCircleRadius(map.current.getZoom()) : 0
                    }
                });

                if (map.current) {
                    circleSource.current = map.current.getSource('circleSource') as GeoJSONSource;
                }
                map.current?.loadImage(
                    giftIcon,
                    (error, image) => {
                        if (error) throw error;
                        map.current!.addImage('gift-icon', image!);
                    }
                );
                map.current?.loadImage(
                    popupImg,
                    (error, image) => {
                        if (error) throw error;
                        map.current!.addImage('popup-icon', image!);
                    }
                );
            });

            map.current.on('zoom', () => {
                if (map.current && circleSource.current) {
                    const zoomLevel = map.current.getZoom();
                    const radiusInPixels = getCircleRadius(zoomLevel);

                    map.current.setPaintProperty('gpsArea', 'circle-radius', radiusInPixels);
                }
            });

            // map.current.on('error', (e) => {
            //     showModal({
            //         id: 'error-map',
            //         showCloseIcon: false,
            //         saveButton: '再読み込み',
            //         cancelButton: false,
            //         content: <div>地図の読み込み中にエラーが発生しました</div>,
            //         onSave: () => {
            //             hideModal('error-map');
            //             window.location.reload();
            //         }
            //     });
            // });

            setLoading(false);
        } catch (error) {
            showModal({
                id: 'error-map',
                showCloseIcon: false,
                saveButton: '再読み込み',
                cancelButton: false,
                content: <div>地図の読み込み中にエラーが発生しました</div>,
                onSave: () => {
                    hideModal('error-map');
                    window.location.reload();
                }
            });
        }
    }, [userLocation]);

    useEffect(() => {
        if (userLocation && map.current && marker.current) {
            marker.current.setLngLat(userLocation);
            map.current.easeTo({
                center: userLocation,
                duration: 1000,
                essential: true,
                speed: 0.2,
                curve: 1
            });

            if (circleSource.current) {
                circleSource.current.setData({
                    type: 'FeatureCollection',
                    features: [
                        {
                            type: 'Feature',
                            geometry: {
                                type: 'Point',
                                coordinates: userLocation
                            },
                            properties: {}
                        }
                    ]
                });
            }
        }
    }, [userLocation]);

    const flyToUserLocation = () => {
        if (map.current && userLocation) {
            map.current.easeTo({
                center: userLocation,
                zoom: 16,
                duration: 1000,
                essential: true
            });
        }
    };

    const handleSubmitAddLocation = (location: Location, checked: boolean) => {
        hideModal('add-point-owner-item');
        const itemJs: OwnerItem | null = itemToSetLocation;
        const listJs: OwnerItem[] | null = listItemToSetLocation;
        if (!itemJs || !listJs || !itemJs.id || listJs.length <= 0) return;
        let newList = listJs.map((it) => {
            return checked ? { ...it, ...location } : (it.id === itemJs.id ? { ...it, ...location } : it);
        });
        offAddPoint();
        setTimeout(() => {
            navigate(ROUTES.setLocationItem.href, { state: { items: JSON.stringify(newList) } });
        }, 240);
    };

    const handleMapClick = async (event: mapboxgl.MapMouseEvent) => {
        const { lng, lat } = event.lngLat;
        if (!itemToSetLocation) return;
        let checked = false;
        const newPoint = {
            type: 'Feature',
            geometry: {
                type: 'Point',
                coordinates: [lng, lat]
            },
            properties: {
                icon: 'gift'
            }
        };
        setMarkers([newPoint]);

        const address = await LocationService.getAddress(lng, lat);
        const location: Location = {
            latitude: lat,
            longitude: lng,
            address: address
        };
        const data: OwnerItem = itemToSetLocation;
        showModal({
            id: 'add-point-owner-item',
            title: '',
            cancelButton: false,
            saveButton: false,
            showCloseIcon: false,
            content: (
                <div className='w-full text-start flex flex-col justify-center items-center'>
                    <img src={mapPinnedImg} alt={mapPinnedImg} className='w-[94px] h-[112px]' />
                    <p className='mt-6 text-[14px] text-gray'>{t('messages.itemPinned', { itemName: handleLongText(data?.item, 7) || '' })}</p>
                    <p className='text-[14px] text-textGreen2 break-words'>{address}</p>
                    <p className='text-[14px]  text-gray'>{t('messages.confirmPin')}</p>
                    <CheckboxComponent className='mt-4' label={t('text.setForAll')} onCheckedChange={value => checked = value} />
                    <div className='flex flex-row text-center mt-6 w-full justify-center space-x-[10px]'>
                        <Button
                            variant='white'
                            onClick={async () => {
                                hideModal('add-point-owner-item');
                            }}
                            className='w-full'
                        >
                            キャンセル
                        </Button>
                        <Button
                            variant='main'
                            onClick={async () => {
                                handleSubmitAddLocation(location, checked);
                            }}
                            className='w-full'
                        >
                            <img src={mapPinIcon} alt={mapPinIcon} className='w-[18px] h-[18px] mr-1' />
                            ここに配置
                        </Button>
                    </div>
                </div>
            )
        });
    };

    const handleAddPoint = () => {
        if (map.current) {
            setOnAddGift(true);
            map.current.on('click', handleMapClick);
        }
    };

    const offAddPoint = () => {
        if (map.current && map.current.getLayer(sourceId)) {
            markers.splice(-1, 1);
            map.current.removeLayer(sourceId);
            map.current.removeSource(sourceId);
        }
        setOnAddGift(false);
        map.current!.off('click', handleMapClick);
    };

    useEffect(() => {
        if (map.current && markers.length > 0 && (onAddGift || isShowingModalMove)) {
            if (!map.current.getLayer(sourceId)) {
                map.current.addSource(sourceId, {
                    type: 'geojson',
                    data: {
                        type: 'FeatureCollection',
                        features: markers
                    }
                });

                map.current.addLayer({
                    id: sourceId,
                    type: 'symbol',
                    source: sourceId,
                    layout: {
                        'icon-image': 'gift-icon',
                        'icon-size': 0.15,
                        'icon-allow-overlap': false,
                        'icon-offset': [0, -128]
                    }
                });
            } else {
                const source = map.current.getSource(sourceId) as mapboxgl.GeoJSONSource;
                source.setData({
                    type: 'FeatureCollection',
                    features: markers
                });
            }
        }
    }, [markers]);

    const handleGetItemNear = async () => {
        if (!userLocation) return;
        if (map.current && map.current.getLayer(sourceIdItemOnMap)) {
            map.current.removeLayer(sourceIdItemOnMap);
            map.current.removeSource(sourceIdItemOnMap);
        }
        setIsShowingModalMove(true);
        showModal({
            id: 'move-to-find',
            cancelButton: false,
            saveButton: false,
            showCloseIcon: true,
            closeWhenOutside: true,
            onCancel: () => {
                setIsShowingModalMove(false);
                if (timeoutNoItem) {
                    clearTimeout(timeoutNoItem);
                    timeoutNoItem = null;
                }
            },
            content: (
                <div className='w-full flex flex-col justify-center items-center'>
                    <img src={moveToFindGif} alt={moveToFindGif} className='w-[30vw] h-[30vw]' />
                    <p className='text-[15px] font-semibold text-gray mt-6'>{t('text.moveToFind')}</p>
                </div>
            )
        });
        getListNearByFromRemote();
    };

    // const getListNearByFromRemote = async () => {
    //     const params: ParamsGetItemNear = userLocation ? { lat: userLocation[1], long: userLocation[0] } : { lat: '', long: '' };
    //     const res = await flowResult(getListNearBy(params));
    //     if (res && res.length > 0) {
    //         await handleVibrate();
    //         setTimeout(() => {
    //             hideModal('move-to-find');
    //             addMarkerItem(res, true);
    //         }, 1500);
    //     } else {
    //         setTimeout(() => {
    //             isShowingModalMove && getListNearByFromRemote();
    //         }, 1000);
    //     }
    // };

    const getListNearByFromRemote = async () => {
        const params: ParamsGetItemNear = userLocation ?
            { lat: userLocation[1], long: userLocation[0] } :
            { lat: '', long: '' };

        // Set timeout sau 2 phút nếu không có item
        if (!timeoutNoItem) {
            timeoutNoItem = setTimeout(() => {
                if (!listNearBy || listNearBy.length === 0) {
                    hideModal('move-to-find'); // Đóng modal loading nếu vẫn mở
                    showModal({
                        id: 'not-found-items',
                        cancelButton: false,
                        saveButton: false,
                        showCloseIcon: true,
                        closeWhenOutside: true,
                        content: (
                            <div className='w-full flex flex-col justify-center items-center p-4'>
                                <p className='text-center text-[15px] font-semibold'>
                                    現在の位置ではアイテムが見つかりませんでした。別のエリアに移動して、もう一度お試しください。
                                </p>
                            </div>
                        )
                    });
                    timeoutNoItem = null;
                    setIsShowingModalMove(false);
                }
            }, 60000); // 2 phút = 120000 ms
        }

        const res = await flowResult(getListNearBy(params));
        if (res && res.length > 0) {
            await handleVibrate();
            setTimeout(() => {
                hideModal('move-to-find');
                addMarkerItem(res, true);
                if (timeoutNoItem) {
                    clearTimeout(timeoutNoItem);
                    timeoutNoItem = null;
                }
            }, 1500);
        } else {
            setTimeout(() => {
                isShowingModalMove && getListNearByFromRemote();
            }, 1000);
        }
    };

    const addMarkerItem = async (listItem: ItemOnMap[] = [], setShowCount = false) => {
        if (!map.current) return;

        const features: any[] = [];

        const loadImage = (src: string) => {
            return new Promise<HTMLImageElement>((resolve, reject) => {
                const img = new Image();
                img.crossOrigin = 'Anonymous';
                img.onload = () => resolve(img);
                img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
                img.src = src;
            });
        };

        for (const e of listItem) {
            let imageUrl = e.file?.path || itemImg; // Mặc định ảnh local

            try {
                await fetch(imageUrl, { method: 'HEAD' }); // Kiểm tra xem ảnh có tồn tại không
            } catch {
                console.warn(`Image not found, using fallback: ${imageUrl}`);
                imageUrl = itemImg; // Nếu lỗi, thay bằng ảnh local
            }

            try {
                const img = await loadImage(imageUrl);
                const canvas = document.createElement('canvas');
                const fixedWidth = 80;
                const fixedHeight = 80;
                const borderRadius = 0.1 * fixedWidth;
                canvas.width = fixedWidth;
                canvas.height = fixedHeight;
                const ctx = canvas.getContext('2d');

                if (ctx) {
                    ctx.beginPath();
                    ctx.moveTo(borderRadius, 0);
                    ctx.lineTo(fixedWidth - borderRadius, 0);
                    ctx.arcTo(fixedWidth, 0, fixedWidth, fixedHeight, borderRadius);
                    ctx.lineTo(fixedWidth, fixedHeight - borderRadius);
                    ctx.arcTo(fixedWidth, fixedHeight, fixedWidth - borderRadius, fixedHeight, borderRadius);
                    ctx.lineTo(borderRadius, fixedHeight);
                    ctx.arcTo(0, fixedHeight, 0, fixedHeight - borderRadius, borderRadius);
                    ctx.lineTo(0, borderRadius);
                    ctx.arcTo(0, 0, borderRadius, 0, borderRadius);
                    ctx.closePath();
                    ctx.clip();
                    ctx.drawImage(img, 0, 0, fixedWidth, fixedHeight);
                }

                const processedSrc = canvas.toDataURL();
                const imageId = `custom-icon-${e.id}`;

                if (!map.current.hasImage(imageId)) {
                    const finalImg = await loadImage(processedSrc);
                    map.current.addImage(imageId, finalImg);
                }

                features.push(
                    {
                        type: 'Feature',
                        geometry: { type: 'Point', coordinates: [e.longitude, e.latitude] },
                        properties: { icon: 'popup-icon', type: 'popup' }
                    },
                    {
                        type: 'Feature',
                        geometry: { type: 'Point', coordinates: [e.longitude, e.latitude] },
                        properties: { icon: imageId, type: 'image' }
                    }
                );
            } catch (error) {
                console.error('Error processing image:', error);
            }
        }

        if (map.current.getSource(sourceIdItemOnMap)) {
            (map.current.getSource(sourceIdItemOnMap) as mapboxgl.GeoJSONSource).setData({
                type: 'FeatureCollection',
                features
            });
        } else {
            map.current.addSource(sourceIdItemOnMap, {
                type: 'geojson',
                data: { type: 'FeatureCollection', features }
            });

            map.current.addLayer({
                id: `${sourceIdItemOnMap}-popup`,
                type: 'symbol',
                source: sourceIdItemOnMap,
                layout: { 'icon-image': ['get', 'icon'], 'icon-size': 1, 'icon-allow-overlap': true, 'icon-offset': [0, -30] },
                filter: ['==', 'type', 'popup']
            });

            map.current.addLayer({
                id: `${sourceIdItemOnMap}-image`,
                type: 'symbol',
                source: sourceIdItemOnMap,
                layout: { 'icon-image': ['get', 'icon'], 'icon-size': 0.6, 'icon-allow-overlap': true, 'icon-offset': [0, -60] },
                filter: ['==', 'type', 'image']
            });
        }

        handleVibrate();
        setIsShowingModalMove(false);
        setShowCountItem(setShowCount);
    };

    const gotoListItems = () => {
        navigate(ROUTES.listItems.href);
    };
    const gotoProfile = () => {
        navigate(ROUTES.profile.href);
    };
    const gotoMyOwnedItems = () => {
        navigate(ROUTES.collectefItems.href);
    };

    const handleShake = () => {
        requestPermission();
    };

    const handleShowModalShake = async () => {
        setShowCountItem(false);
        handleShake();
        showModal({
            id: 'shake-to-get',
            showCloseIcon: true,
            saveButton: false,
            cancelButton: false,
            onCancel: () => {
                if (map.current && map.current.getLayer(sourceIdItemOnMap)) {
                    map.current.removeLayer(sourceIdItemOnMap);
                    map.current.removeSource(sourceIdItemOnMap);
                }
                setIsShakeEnabled(false);
            },
            content: (
                <div className='w-full flex flex-col justify-center items-center'>
                    <img src={shakeToGetGif} alt={shakeToGetGif} className='w-[30vw] h-[30vw]' />
                    <p className='text-[15px] font-semibold text-gray mt-6'>{t('text.shakeToGet')}</p>
                </div>
            )
        });
        // handleWhenShake();
    };
    return (
        <div className='w-[100vw] h-[100dvh] absolute top-0 left-0 bg-bgMain'>
            <button onClick={playHapticSound} style={{ display: 'none' }} id='hidden-audio-trigger'>Bật âm thanh</button>
            <div className='absolute top-[24px] left-[16px] right-[16px] z-50 w-[calc(100vw_-_32px)] flex justify-between'>
                <div className='bg-white w-[180px] h-[52px] flex flex-row gap-2 justify-center items-center rounded-[14px]'>
                    <img src={logoIcon} alt='logo-img' className='w-[148px] h-[32px]' />
                </div>
                <div>
                    <button onClick={gotoProfile} className='bg-white w-[52px] h-[52px] rounded-full flex justify-center items-center'>
                        <img src={menuIconSvg} alt='menu-icon' className='w-[24px] h-[24px]' />
                    </button>
                    <button
                        onClick={flyToUserLocation}
                        className=' bg-white w-[52px] h-[52px] rounded-full flex justify-center items-center mt-[12px]'
                    >
                        <LocateFixed size={24} className={`${userLocation ? 'text-blue-500' : 'text-blue-500'}`} />
                    </button>
                </div>

            </div>
            <div className='absolute bottom-[30px] left-1/2 -translate-x-1/2 z-50 flex space-x-6'>
                <button onClick={gotoListItems} className='bg-white w-[80px] h-[80px] rounded-[22px] flex flex-col justify-center items-center text-center'>
                    <div className='w-full flex justify-center items-center'>
                        <img src={markerIcon} alt={markerIcon} className='w-[30px] h-[30px]' />
                    </div>
                    <span className='text-[13px] text-text mt-[6px]'>{t('text.place')}</span>
                </button>
                <button onClick={handleGetItemNear} className='bg-white w-[80px] h-[80px] rounded-[22px] flex flex-col justify-center text-center  items-center'>
                    <div className='w-full flex justify-center items-center'>
                        <img src={dropIcon} alt={dropIcon} className='w-[30px] h-[30px]' />
                    </div>
                    <span className='text-[13px] text-text mt-[6px]'>{t('text.search')}</span>
                </button>
                <button onClick={gotoMyOwnedItems} className='bg-white w-[80px] h-[80px] rounded-[22px] flex flex-col justify-center text-center  items-center'>
                    <div className='w-full flex justify-center items-center'>
                        <img src={stackIcon} alt={stackIcon} className='w-[30px] h-[30px]' />
                    </div>
                    <span className='text-[13px] text-text mt-[6px]'>{t('text.box')}</span>
                </button>
            </div>
            <div className={cn('w-[calc(100vw-32px)] mx-4 h-[68px] absolute bottom-[122px] left-0 bg-notiBg/70 text-white text-[13px] font-semibold flex-col justify-center items-center rounded-full hidden', (listNearBy && showCountItem && listNearBy.length > 0) && 'z-50 flex')}>
                {listNearBy?.length > 0 ?
                    (
                        <>
                            <p>{t('text.findXItem', { itemsCount: listNearBy.length })}</p>
                            <button onClick={handleShowModalShake} className='flex mt-[10px]'>
                                <img src={imgArrowRight} alt={imgArrowRight} className='w-[18px] h-[18px]' />
                                <p className='ml-[6px]'>取得する</p>
                            </button>
                        </>
                    ) :
                    (
                        <p className='text-center'>
                            現在の位置ではアイテムが見つかりませんでした。別のエリアに移動して、もう一度お試しください。
                        </p>
                    )}
            </div>
            <div ref={mapContainer} className='w-[100vw] h-[100vh]' />
        </div>
    );
});
